import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { GYPSUM } from 'HubStyleTokens/colors';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage'; // @ts-expect-error untyped dependency

import { postNotification } from 'ReduxMessenger/actions/NotificationActions';
import H1 from 'UIComponents/elements/headings/H1';
import H2 from 'UIComponents/elements/headings/H2';
import H5 from 'UIComponents/elements/headings/H5';
import Small from 'UIComponents/elements/Small';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIBox from 'UIComponents/layout/UIBox';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIImage from 'UIComponents/image/UIImage';
import UILink from 'UIComponents/link/UILink';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIModal from 'UIComponents/dialog/UIModal';
import UISection from 'UIComponents/section/UISection';
import UISelectableBox from 'UIComponents/button/UISelectableBox';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import { formatCentsToDollars } from 'marketplace-ui-common/utils/currency';
import * as PostMessageType from 'marketplace-embed/constants/PostMessageType';
import * as RequestStates from 'ecosystem-marketplace-core/constants/RequestState';
import { isPending, isFailed, isSucceeded } from 'ecosystem-marketplace-core/utils/requestState';
import { getDownloadedAssetsSuccessUrl } from 'ecosystem-marketplace-core/assets/utils/urls'; // @ts-expect-error untyped dependency

import PayPalIcon from 'ecosystem-marketplace-core/components/logos/PayPalLogo'; // @ts-expect-error untyped dependency

import * as PaymentType from '../../constants/PaymentType';
import { getDetailPageFullUrl } from '../../utils/urls';
import { purchase } from '../../clients/ecosystemClient'; // @ts-expect-error untyped dependency

import { useEmbedContext } from '../../hooks/useEmbedContext';
import CurrencyText from './CurrencyText';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
const HUBSPOT_PRIVACY_POLICY_LINK = 'https://legal.hubspot.com/privacy-policy';
const SUBSCREEN = 'order summary modal';

const NoopExternalIcon = () => null;

const ExternalLink = props => /*#__PURE__*/_jsx(UILink, Object.assign({}, props, {
  ExternalIcon: NoopExternalIcon,
  external: true
}));

const SelectableRadioInput = (_ref) => {
  let {
    children
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["children"]);

  return /*#__PURE__*/_jsx(UISelectableBox, Object.assign({}, rest, {
    selectionMarkLocation: "left",
    style: {
      width: '50%'
    },
    type: "radio",
    children: /*#__PURE__*/_jsx(UIFlex, {
      className: "p-left-5",
      direction: "row",
      align: "center",
      children: children
    })
  }));
};

const handleStripeCheckoutError = result => {
  postNotification({
    id: 'STRIPE_CHECKOUT_ERROR',
    type: 'danger',
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.details.orderSummaryModal.errorTitle"
    }),
    message: result.error.message
  });
};

const handleGenericCheckoutError = () => postNotification({
  type: 'danger',
  titleText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.details.orderSummaryModal.errorTitle"
  }),
  message: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.details.orderSummaryModal.errorBody"
  })
});

export default function AssetOrderSummaryModal({
  assetDetails,
  offeringType,
  onClose
}) {
  const {
    name,
    offering: {
      slug
    },
    offeringId,
    paypalEnabled,
    provider: {
      name: providerName
    },
    pricing: {
      cents,
      planType
    },
    privacyUrl,
    stripeEnabled,
    termsUrl,
    thumbnailUrl
  } = assetDetails;
  const incomingPaymentType = useMemo(() => {
    if (planType === 'FREE') {
      return PaymentType.FREE;
    } else if (stripeEnabled && !paypalEnabled) {
      return PaymentType.STRIPE;
    } else if (paypalEnabled && !stripeEnabled) {
      return PaymentType.PAYPAL;
    }

    return null;
  }, [paypalEnabled, planType, stripeEnabled]);
  const {
    trackBeforeUnloadEvent,
    trackInteraction,
    trackPageview
  } = useContext(StorefrontUsageTrackerContext);
  const [purchaseRequestState, setPurchaseRequestState] = useState(RequestStates.UNINITIALIZED);
  const [selectedPaymentType, setSelectedPaymentType] = useState(incomingPaymentType);
  const handleTrackInteraction = useCallback((action, eventProperties = {}) => {
    trackInteraction(action, Object.assign({}, eventProperties, {
      subscreen: SUBSCREEN
    }));
  }, [trackInteraction]);
  const handleTrackInstallUsage = useCallback(() => {
    let paymentType;

    if (selectedPaymentType === PaymentType.FREE) {
      paymentType = 'free';
    } else if (selectedPaymentType === PaymentType.STRIPE) {
      paymentType = 'stripe';
    } else if (selectedPaymentType === PaymentType.PAYPAL) {
      paymentType = 'paypal';
    }

    trackBeforeUnloadEvent('details-click-install', {
      subscreen: SUBSCREEN,
      paymentType,
      value: formatCentsToDollars(cents)
    });
  }, [cents, selectedPaymentType, trackBeforeUnloadEvent]);
  const handleEmbedMessage = useCallback(message => {
    const {
      payload: {
        type
      }
    } = message,
          restPayload = _objectWithoutPropertiesLoose(message.payload, ["type"]);

    switch (type) {
      case PostMessageType.STRIPE_CHECKOUT_ERROR:
        {
          handleStripeCheckoutError(restPayload);
          break;
        }

      default:
        break;
    }
  }, []);
  const {
    isEmbedded,
    pageName,
    sendMessage,
    siteDomain
  } = useEmbedContext(handleEmbedMessage);
  const getInstallRedirectUrl = useCallback(() => getDownloadedAssetsSuccessUrl({
    downloadedOfferingId: offeringId.toString(),
    pageName,
    siteDomain
  }, offeringType), [offeringId, offeringType, pageName, siteDomain]);
  const handleInstall = useCallback(() => {
    trackInteraction('click install confirm button');
    setPurchaseRequestState(RequestStates.PENDING);
    const purchaseInfo = {
      offeringId,
      paymentType: selectedPaymentType
    };

    if (selectedPaymentType === PaymentType.STRIPE || selectedPaymentType === PaymentType.PAYPAL) {
      purchaseInfo.successUrl = getInstallRedirectUrl();
      purchaseInfo.cancelUrl = getDetailPageFullUrl(offeringType, slug);
    }

    return purchase(purchaseInfo).then(({
      stripeAccountId,
      stripePublishableKey,
      stripeSessionId,
      paypalCheckoutUrl
    }) => {
      handleTrackInstallUsage();
      setPurchaseRequestState(RequestStates.SUCCEEDED);

      if (selectedPaymentType === PaymentType.FREE) {
        const redirectUrl = getInstallRedirectUrl();

        if (isEmbedded) {
          sendMessage(PostMessageType.REDIRECT_TO_URL, {
            redirectUrl
          });
        } else {
          window.location.replace(redirectUrl);
        }
      } else if (selectedPaymentType === PaymentType.PAYPAL) {
        if (isEmbedded) {
          sendMessage(PostMessageType.REDIRECT_TO_URL, {
            redirectUrl: paypalCheckoutUrl
          });
        } else {
          window.location.replace(paypalCheckoutUrl);
        }
      } else if (selectedPaymentType === PaymentType.STRIPE) {
        if (isEmbedded) {
          sendMessage(PostMessageType.REDIRECT_TO_STRIPE_CHECKOUT, {
            stripeAccountId,
            stripePublishableKey,
            stripeSessionId
          });
        } else {
          const stripe = window.Stripe(stripePublishableKey, {
            stripeAccount: stripeAccountId
          });
          stripe.redirectToCheckout({
            sessionId: stripeSessionId
          }).then(result => {
            setPurchaseRequestState(RequestStates.FAILED);
            handleStripeCheckoutError(result);
          }).catch(error => {
            setPurchaseRequestState(RequestStates.FAILED);
            console.error('error with Stripe purchase\n', error);
          });
        }
      }
    }).catch(error => {
      setPurchaseRequestState(RequestStates.FAILED);
      handleGenericCheckoutError();
      console.error('error initializing purchase\n', error);
    });
  }, [getInstallRedirectUrl, handleTrackInstallUsage, isEmbedded, offeringId, offeringType, selectedPaymentType, sendMessage, slug, trackInteraction]);
  const renderedCurrency = useMemo(() => {
    if (planType === 'FREE') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.details.orderSummaryModal.free"
      });
    }

    return /*#__PURE__*/_jsx(CurrencyText, {
      cents: cents
    });
  }, [cents, planType]);
  const [checked, setChecked] = useState(false);
  const renderedDisclaimerPolicy = useMemo(() => {
    const handleChange = () => {
      setChecked(!checked);
    };

    return /*#__PURE__*/_jsx(UICheckbox, {
      checked: checked,
      className: "m-bottom-4",
      onChange: handleChange,
      "data-test-id": "free-install-disclaimer-checkbox",
      children: /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "marketplace-assets-ui.details.orderSummaryModal.thirdPartyDisclaimer_jsx",
          elements: {
            Link: ExternalLink
          },
          options: {
            termsLink: {
              href: termsUrl,
              onClick: () => {
                trackInteraction('click terms link');
              }
            },
            providerPrivacyPolicyLink: {
              href: privacyUrl,
              onClick: () => {
                trackInteraction('click privacy policy link');
              }
            }
          }
        })
      })
    });
  }, [checked, privacyUrl, termsUrl, trackInteraction]);
  const renderedPaymentMethods = useMemo(() => {
    if (planType === 'FREE') {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [renderedDisclaimerPolicy, /*#__PURE__*/_jsx(UILoadingButton, {
          "data-test-id": "free-install-confirm-button",
          "data-succeeded": isSucceeded(purchaseRequestState),
          disabled: !checked,
          failed: isFailed(purchaseRequestState),
          loading: isPending(purchaseRequestState),
          onClick: () => handleInstall(),
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.details.orderSummaryModal.freeButton"
          })
        })]
      });
    }

    return /*#__PURE__*/_jsxs("div", {
      style: {
        width: '100%'
      },
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.details.orderSummaryModal.paymentMethod"
        })
      }), /*#__PURE__*/_jsxs(UIToggleGroup, {
        name: "paymentMethod",
        className: "flex-row m-bottom-6",
        children: [stripeEnabled && /*#__PURE__*/_jsx(SelectableRadioInput, {
          className: "m-right-3",
          name: PaymentType.STRIPE,
          onClick: () => {
            setSelectedPaymentType(PaymentType.STRIPE);
            handleTrackInteraction('select payment type', {
              value: PaymentType.STRIPE
            });
          },
          selected: selectedPaymentType === PaymentType.STRIPE,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.details.orderSummaryModal.creditCard"
          })
        }), paypalEnabled && /*#__PURE__*/_jsx(SelectableRadioInput, {
          name: PaymentType.PAYPAL,
          onClick: () => {
            setSelectedPaymentType(PaymentType.PAYPAL);
            handleTrackInteraction('select payment type', {
              value: PaymentType.PAYPAL
            });
          },
          selected: selectedPaymentType === PaymentType.PAYPAL,
          children: /*#__PURE__*/_jsx(PayPalIcon, {
            size: "sm"
          })
        })]
      }), /*#__PURE__*/_jsx(UILoadingButton, {
        use: "primary",
        disabled: selectedPaymentType !== PaymentType.STRIPE && selectedPaymentType !== PaymentType.PAYPAL,
        loading: isPending(purchaseRequestState),
        failed: isFailed(purchaseRequestState),
        onClick: () => handleInstall(),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.details.orderSummaryModal.paidButton"
        })
      })]
    });
  }, [checked, handleInstall, handleTrackInteraction, paypalEnabled, planType, purchaseRequestState, renderedDisclaimerPolicy, selectedPaymentType, stripeEnabled]);
  useEffect(() => {
    trackPageview({
      subscreen: SUBSCREEN
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return /*#__PURE__*/_jsxs(UIModal, {
    "data-test-id": "theme-order-summary-modal",
    width: 600,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.details.orderSummaryModal.title"
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: () => onClose()
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      className: "p-all-0",
      children: [/*#__PURE__*/_jsx("div", {
        className: "p-y-8 p-x-10",
        style: {
          backgroundColor: GYPSUM
        },
        children: /*#__PURE__*/_jsxs(UIFlex, {
          direction: "row",
          children: [/*#__PURE__*/_jsx(UIBox, {
            grow: 1,
            children: /*#__PURE__*/_jsxs(UIMediaObject, {
              itemLeft: /*#__PURE__*/_jsx(UIImage, {
                height: 110,
                responsive: false,
                src: thumbnailUrl
              }),
              spacing: "medium",
              children: [/*#__PURE__*/_jsx(H5, {
                className: "m-bottom-1",
                children: name
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                className: "display-block m-bottom-2",
                message: "marketplace-assets-ui.details.orderSummaryModal.providerName",
                options: {
                  providerName
                }
              }), /*#__PURE__*/_jsx(UIBadge, {
                use: "koala",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `marketplace-assets-ui.details.offeringType.${offeringType}`
                })
              })]
            })
          }), /*#__PURE__*/_jsx(UIBox, {
            shrink: 0,
            children: /*#__PURE__*/_jsx(H1, {
              children: renderedCurrency
            })
          })]
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: "p-y-8 p-x-10",
        children: [/*#__PURE__*/_jsx(UISection, {
          children: renderedPaymentMethods
        }), /*#__PURE__*/_jsx(UISection, {
          className: "m-bottom-0",
          children: /*#__PURE__*/_jsx(Small, {
            use: "help",
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "marketplace-assets-ui.details.orderSummaryModal.legalDisclaimer_jsx",
              elements: {
                Link: ExternalLink
              },
              options: {
                providerName,
                hubspotPrivacyPolicyLink: {
                  href: HUBSPOT_PRIVACY_POLICY_LINK,
                  onClick: () => {
                    trackInteraction('click product privacy policy link');
                  }
                },
                providerPrivacyPolicyLink: {
                  href: privacyUrl,
                  onClick: () => {
                    trackInteraction('click privacy policy link');
                  }
                }
              }
            })
          })
        })]
      })]
    })]
  });
}